import { CreateSlaPolicyPayload, ReqParams, SlaPolicyResponse } from "@/types";
import axiosInstance from "./axiosInstance";

const omitBlankSearch = (params: ReqParams): ReqParams => {
  const { search, ...requestParams } = params;
  return typeof search === "string" && search.trim() === "" ? requestParams : params;
};

const normalizeBooleanFilter = (value: unknown): unknown => {
  if (value === "true") return true;
  if (value === "false") return false;
  return value;
};

const getDateFilter = (filters: ReqParams[]) =>
  filters.find(
    (filter) => filter?.startDate !== undefined || filter?.endDate !== undefined,
  );

const normalizeGroupListParams = (params: ReqParams): ReqParams => {
  const {
    filters,
    skipGlobalLoader: _skipGlobalLoader,
    ...requestParams
  } = omitBlankSearch(params);

  if (!Array.isArray(filters)) return requestParams;

  const dateFilter = getDateFilter(filters);
  const status = filters.find((filter) => filter?.status !== undefined)?.status;

  return {
    ...requestParams,
    ...(dateFilter?.startDate !== undefined
      ? { startDate: dateFilter.startDate }
      : {}),
    ...(dateFilter?.endDate !== undefined ? { endDate: dateFilter.endDate } : {}),
    ...(status !== undefined ? { status: normalizeBooleanFilter(status) } : {}),
  };
};

const prepareMemberListParams = (params: ReqParams): ReqParams => {
  const {
    skipGlobalLoader: _skipGlobalLoader,
    ...requestParams
  } = omitBlankSearch(params);

  // CustomTable already owns the member-list filter contract. Preserve its
  // filters array and values verbatim; only strip client-only request metadata.
  return requestParams;
};

const normalizeGroupTicketQueueParams = (params: ReqParams): ReqParams => {
  const {
    filters,
    skipGlobalLoader: _skipGlobalLoader,
    ...requestParams
  } = omitBlankSearch(params);

  const { groupId: requestedGroupId, ...rest } = requestParams;
  const assignment = Array.isArray(filters)
    ? filters.find((filter) => filter?.assignment !== undefined)?.assignment
    : undefined;
  // The drawer's Status select filters on the ticket's group workflow state.
  const groupStatus = Array.isArray(filters)
    ? filters.find((filter) => filter?.status !== undefined)?.status
    : undefined;
  const dateFilter = Array.isArray(filters) ? getDateFilter(filters) : undefined;
  const priority = Array.isArray(filters)
    ? filters.find((filter) => filter?.priority !== undefined)?.priority
    : undefined;
  const slaStatus = Array.isArray(filters)
    ? filters.find((filter) => filter?.slaStatus !== undefined)?.slaStatus
    : undefined;

  // A System Admin opens the queue for one group from the Groups list; members
  // and group managers send no group and are scoped server-side.
  const groupId = Number(
    requestedGroupId
    ?? (Array.isArray(filters)
      ? filters.find((filter) => filter?.groupId !== undefined)?.groupId
      : undefined),
  );

  // The queue flattens the filters it owns, but the drawer's group-member filter
  // travels in the `filters` array exactly as the other ticket tables send it.
  const passthroughFilters = Array.isArray(filters)
    ? filters.filter((filter) => filter?.memberId !== undefined)
    : [];

  return {
    ...rest,
    ...(Number.isFinite(groupId) && groupId > 0 ? { groupId } : {}),
    ...(passthroughFilters.length ? { filters: passthroughFilters } : {}),
    // The queue always sends an explicit assignment; "all" is the default view.
    assignment: assignment ?? "all",
    ...(groupStatus !== undefined ? { groupStatus } : {}),
    ...(priority !== undefined ? { priority } : {}),
    ...(slaStatus !== undefined ? { slaStatus } : {}),
    ...(dateFilter?.startDate !== undefined
      ? { startDate: dateFilter.startDate }
      : {}),
    ...(dateFilter?.endDate !== undefined ? { endDate: dateFilter.endDate } : {}),
  };
};

const normalizeSlaTicketParams = (params: ReqParams): ReqParams => {
  const {
    filters,
    skipGlobalLoader: _skipGlobalLoader,
    ...requestParams
  } = omitBlankSearch(params);

  if (!Array.isArray(filters)) return requestParams;

  const readFilter = (key: string) =>
    filters.find((filter) => ![undefined, null, ""].includes(filter?.[key]))?.[key];
  const statuses = filters
    .map((filter) => filter?.status)
    .filter((status): status is string => typeof status === "string" && status !== "");
  const dateFilter = getDateFilter(filters);

  // The endpoint takes flat query fields; the table's filters array is not sent.
  const flattened: ReqParams = {
    priority: readFilter("priority"),
    slaStatus: readFilter("slaStatus"),
    // The drawer's Status select is multi-select; the endpoint takes one value.
    status: statuses.length ? statuses.join(",") : undefined,
    categoryId: readFilter("ticketCategoryId"),
    groupId: readFilter("groupId"),
    memberId: readFilter("memberId"),
    fromDate: dateFilter?.startDate,
    toDate: dateFilter?.endDate,
  };

  return {
    ...requestParams,
    ...Object.fromEntries(
      Object.entries(flattened).filter(([, value]) => value !== undefined),
    ),
  };
};

const normalizeMemberAssignedTicketParams = (params: ReqParams): ReqParams => {
  const {
    filters,
    page,
    limit,
    search,
    sortField,
    sortOrder,
  } = omitBlankSearch(params);
  const requestParams = {
    ...(page !== undefined ? { page } : {}),
    ...(limit !== undefined ? { limit } : {}),
    ...(search !== undefined ? { search } : {}),
    ...(sortField !== undefined ? { sortField } : {}),
    ...(sortOrder !== undefined ? { sortOrder } : {}),
  };

  if (!Array.isArray(filters)) return requestParams;

  // Member tickets share the queue's Status select, which reads the group workflow state.
  const groupStatus = filters.find((filter) => filter?.status !== undefined)?.status;
  const dateFilter = getDateFilter(filters);
  const priority = filters.find((filter) => filter?.priority !== undefined)?.priority;
  const slaStatus = filters.find((filter) => filter?.slaStatus !== undefined)?.slaStatus;

  return {
    ...requestParams,
    ...(groupStatus !== undefined ? { groupStatus } : {}),
    ...(priority !== undefined ? { priority } : {}),
    ...(slaStatus !== undefined ? { slaStatus } : {}),
    ...(dateFilter?.startDate !== undefined
      ? { startDate: dateFilter.startDate }
      : {}),
    ...(dateFilter?.endDate !== undefined ? { endDate: dateFilter.endDate } : {}),
  };
};

export default class SupervisorService {
  static async add(params: ReqParams = {}) {
    const response = await axiosInstance.post("/admin/company", params);
    return response.data;
  }
  static async getList(params: ReqParams = {}) {
    const response = await axiosInstance.get("/admin/tenants", { params });
    return response.data;
  }

  static async getDetails(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/admin/tenant/${params.id}`, { params });
    return response.data;
  }

  static async update(params: ReqParams = {}) {
    const response = await axiosInstance.patch(`/admin/tenant/${params.id}`, params);
    return response.data;
  }

  static async updateProfile(params: ReqParams = {}) {
    const response = await axiosInstance.patch("/tenant/update-profile", params);
    return response.data;
  }

  static async deactivateTenant(params: ReqParams = {}) {
    const response = await axiosInstance.post(`/admin/tenants/${params.tenantId}/deactivate`, {
      reason: params.reason || '',
      responseDeadlineDays: params.responseDeadlineDays || 30
    }, { skipGlobalLoader: true });
    return response.data;
  }

  static async reactivateTenant(params: ReqParams = {}) {
    const response = await axiosInstance.post(`/admin/tenants/${params.tenantId}/reactivate`, undefined, { skipGlobalLoader: true });
    return response.data;
  }

  static async permanentlyDeleteTenant(params: ReqParams = {}) {
    const response = await axiosInstance.delete(`/admin/tenants/${params.tenantId}/permanently-delete`, {
      data: {
        reason: params.reason || '',
        force: params.force || false
      },
      skipGlobalLoader: true,
    });
    return response.data;
  }

  static async checkTenantDeactivationDeadline(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/admin/tenants/${params.tenantId}/deactivation-deadline`);
    return response.data;
  }

  static async updateTicketSettings(params: ReqParams = {}) {
    const response = await axiosInstance.post("/tenant/manage-tenant-setting", params);
    return response.data;
  }

  static async getTenantSetting(params: ReqParams = {}) {
    const response = await axiosInstance.get("/tenant/get-tenant-setting", { params });
    return response.data;
  }

  static async getUploadLink(params: ReqParams = {}) {
    const response = await axiosInstance.post(`/attachment-upload`, params, { skipGlobalLoader: true });
    return response.data;
  }

  static async getCsatOverview(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/tenant/reports/csat-overview`, { params });
    return response.data;
  }
  static async getCsatScoreBreakdown(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/tenant/reports/csat-score-breakdown`, { params });
    return response.data;
  }
  static async getCsatByServiceCategory(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/tenant/reports/csat-by-service-category`, { params });
    return response.data;
  }
  static async getCsatTrendAnalysis(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/tenant/reports/csat-trend-analysis`, { params });
    return response.data;
  }

  static async updateCompanyLogo(params: ReqParams = {}) {
    const response = await axiosInstance.patch("/tenant/upload-logo", params);
    return response.data;
  }

  static async setUpEmail(params: ReqParams = {}) {
    const response = await axiosInstance.post("/tenant/add-support-email", params);
    return response.data;
  }

  static async setUpAnyEmailDomain(params: ReqParams = {}) {
    const response = await axiosInstance.post("/tenant/add-support-email-all-domain", params);
    return response.data;
  }

  static async updateEmailSetup(params: ReqParams = {}) {
    const response = await axiosInstance.patch("/tenant/support-email-config", params);
    return response.data;
  }

  static async completeMicrosoftOAuth(params: ReqParams = {}) {
    const response = await axiosInstance.post("/tenant/microsoft/oauth/complete", params);
    return response.data;
  }

  static async revokeMicrosoftOAuth(params: ReqParams = {}) {
    const response = await axiosInstance.delete("/tenant/microsoft/oauth/revoke", { data: params, skipGlobalLoader: true });
    return response.data;
  }

  static async checkChatbotByUUID(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/chatbot/${params.id}`);
    return response.data;
  }


  static async talkWithChatBot(params: ReqParams = {}) {
    const response = await axiosInstance.post("/chatbot-new-ticket", params);
    return response.data;
  }

  static async updateChatBotQuestions(params: ReqParams = {}) {
    const response = await axiosInstance.patch("/tenant/manage-chatbot-questions", params);
    return response.data;
  }

  static async getCompanyDetails(params: ReqParams = {}) {
    const response = await axiosInstance.get("/tenant/company-details", { params });
    return response.data;
  }

  static async importCustomersFromCSV(file: File) {
    const formData = new FormData();
    formData.append('file', file);

    const response = await axiosInstance.post("/tenant/import-customers", formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      skipGlobalLoader: true,
    });
    return response.data;
  }

  // Custom Scripts Methods
  static async createCustomScript(params: ReqParams = {}) {
    const response = await axiosInstance.post("/tenant/custom-scripts", params);
    return response.data;
  }

  static async getCustomScripts(params: ReqParams = {}) {
    const response = await axiosInstance.get("/tenant/custom-scripts", { params });
    return response.data;
  }

  static async updateCustomScript(id: number, params: ReqParams = {}) {
    const response = await axiosInstance.put(`/tenant/custom-scripts/${id}`, params);
    return response.data;
  }

  static async deleteCustomScript(id: number) {
    const response = await axiosInstance.delete(`/tenant/custom-scripts/${id}`, { skipGlobalLoader: true });
    return response.data;
  }

  static async trackScriptUsage(id: number) {
    const response = await axiosInstance.post(`/tenant/custom-scripts/${id}/track-usage`, undefined, { skipGlobalLoader: true });
    return response.data;
  }

  static async createDefaultScripts() {
    const response = await axiosInstance.post("/tenant/create-default-scripts", undefined, { skipGlobalLoader: true });
    return response.data;
  }

  static async getAgentScripts() {
    const response = await axiosInstance.get("/agent/scripts");
    return response.data;
  }
  static async addKnowledgeBase(params: ReqParams = {}) {
    const response = await axiosInstance.post("/tenant/add-knowledgeBase", params);
    return response.data;
  }
  static async getKnowledgeBase(params: ReqParams = {}) {
    const response = await axiosInstance.get("/tenant/knowledgeBase-list", { params });
    return response.data;
  }
  static async updateFeedbackSetting(params: ReqParams = {}) {
    const response = await axiosInstance.post("/tenant/manage-tenant-setting", params);
    return response.data;
  }

  // Toggle auto ticket creation for Facebook / Instagram DM conversations.
  // Send one flag at a time, e.g. { facebookDmAutoTicketCreation: false }
  static async updateSocialDmAutoTicketSetting(params: ReqParams = {}) {
    const response = await axiosInstance.post("/tenant/manage-tenant-setting", params);
    return response.data;
  }

  // Provisional: endpoint and payload keys will change once the backend ships service hours.
  // e.g. { serviceHoursType: 'custom', fromTime: '09:00', toTime: '18:00' }
  static async updateServiceHours(params: ReqParams = {}) {
    const response = await axiosInstance.post("/tenant/manage-tenant-setting", params);
    return response.data;
  }

  static async updateCategoryPriorityMappingSetting(params: ReqParams = {}) {
    const response = await axiosInstance.post("/tenant/manage-tenant-setting", params);
    return response.data;
  }

  static async getFeedbackSubmissions(params: ReqParams = {}) {
    const response = await axiosInstance.get("/tenant/feedback/submissions", { params });
    return response.data;
  }

  static async getDashboardCount(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/tenant/dashboard/count?fromDate=${params.startDate}&toDate=${params.endDate}`);
    return response.data;
  }

  static async addKnowledgeBaseData(params: ReqParams = {}) {
    const response = await axiosInstance.post("tenant/create-knowledgeBase", params);
    return response.data;
  }

  static async updateKnowledgeBaseData(params: ReqParams = {}) {
    const response = await axiosInstance.post("tenant/update-knowledgeBase", params);
    return response.data;
  }

  static async deleteKnowledgeBase(id: number) {
    const response = await axiosInstance.delete(`tenant/delete-knowledgeBase/${id}`, { skipGlobalLoader: true });
    return response.data;
  }

  static async getAgentReport(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/tenant/agent-reports-list`, { params });
    return response.data;
  }

  static async getAgentTicketReport(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/tenant/agent-ticket-reports`, { params });
    return response.data;
  }

  static async getAgentTicketReportDetail(params: ReqParams = {}, id: number) {
    const response = await axiosInstance.get(`/tenant/agent-ticket-reports/${id}`, { params });
    return response.data;
  }

  static async getAgentWorkloadGraph(agentId: number) {
    const response = await axiosInstance.get(`/tenant/agent-workload-graph/${agentId}`);
    return response.data;
  }

  static async getAgentInquery(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/agent/inquiries`, { params });
    return response.data;
  }

  static async moveConversion(params: ReqParams = {}) {
    const response = await axiosInstance.post(`/tenant/conversions/move`, params, { skipGlobalLoader: true });
    return response.data;
  }

  static async getCustomerList(params: ReqParams = {}) {
    const response = await axiosInstance.get("/crm/tenant/customer-list", { params });
    return response.data;
  }

  static async getCustomerData(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/crm/tenant/view-customer/${params.id}`);
    return response.data;
  }

  static async AddCustomer(params: ReqParams = {}) {
    const response = await axiosInstance.post("/crm/tenant/create-customer", params);
    return response.data;
  }

  static async editCustomer(params: ReqParams = {}, id: number) {
    const response = await axiosInstance.patch(`/crm/tenant/edit-customer/${id}`, params);
    return response.data;
  }

  static async getRequestList(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/crm/tenant/customer-update-request-list`, { params });
    return response.data;
  }

  static async updateRequestStatus(params: ReqParams = {}, id: number) {
    const response = await axiosInstance.patch(`/crm/tenant/review-customer-change-request/${id}`, params, { skipGlobalLoader: true });
    return response.data;
  }

  static async getCustomerInteractionList(id: number) {
    const response = await axiosInstance.get(`/crm/customer-detailed-view/${id}`);
    return response.data;
  }

  static async getEmailVariableList() {
    const response = await axiosInstance.get(`/tenant/email/variable-list`);
    return response.data;
  }

  static async getEmailTemplate(id: number) {
    const response = await axiosInstance.get(`/tenant/email/template-view/${id}`);
    return response.data;
  }

  static async getEmailTemplateList(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/tenant/email/template-list`, { params });
    return response.data;
  }

  static async updateEmailTemplate(id: number, params: ReqParams = {}) {
    const response = await axiosInstance.patch(`/tenant/email/edit-template/${id}`, params);
    return response.data;
  }

  static async addEmailTemplate(params: ReqParams = {}) {
    const response = await axiosInstance.post(`/tenant/email/create-template`, params);
    return response.data;
  }

  static async getProspectList(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/tenant/prospect/list`, { params });
    return response.data;
  }

  static async addSingleProspect(params: ReqParams = {}) {
    const response = await axiosInstance.post(`/tenant/prospect/add`, params);
    return response.data;
  }

  static async updateSingleProspect(params: ReqParams = {}, id: number) {
    const response = await axiosInstance.put(`/tenant/prospect/edit/${id}`, params);
    return response.data;
  }

  static async validateImportCsv(formData: FormData) {
    const response = await axiosInstance.post(`/tenant/validate-import-prospect`, formData, { skipGlobalLoader: true });
    return response.data;
  }

  static async ImportProspectSubmit(formData: FormData) {
    const response = await axiosInstance.post(`/tenant/import-prospect`, formData, { skipGlobalLoader: true });
    return response.data;
  }

  // Custom Field Management Methods
  static async addCustomFields(params: ReqParams = {}) {
    const response = await axiosInstance.post("/tenant/custom-field/add", params);
    return response.data;
  }

  static async getCustomFields(tenantId: string, params: ReqParams = {}) {
    const response = await axiosInstance.get(`/tenant/custom-field/list/${tenantId}`, { params });
    return response.data;
  }

  static async updateCustomField(fieldId: string, params: ReqParams = {}) {
    const response = await axiosInstance.put(`/tenant/custom-field/update/${fieldId}`, params);
    return response.data;
  }

  static async deleteCustomField(fieldId: string) {
    const response = await axiosInstance.delete(`/tenant/custom-field/delete/${fieldId}`, { skipGlobalLoader: true });
    return response.data;
  }

  static async addCustomFieldValues(params: ReqParams = {}) {
    const response = await axiosInstance.post("/tenant/custom-field-value/add", params);
    return response.data;
  }

  static async getCustomFieldDetails(fieldId: string) {
    const response = await axiosInstance.get(`/tenant/custom-field-details/${fieldId}`);
    return response.data;
  }

  static async getSegmentList(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/tenant/segments/list`, { params });
    return response.data;
  }

  static async addSegment(params: ReqParams = {}) {
    const response = await axiosInstance.post(`/tenant/segments/add`, params);
    return response.data;
  }

  static async updateSegment(params: ReqParams = {}, id: number) {
    const response = await axiosInstance.patch(`/tenant/segments/edit/${id}`, params);
    return response.data;
  }

  static async getSegmentDetails(id: number) {
    const response = await axiosInstance.get(`/tenant/segments/view/${id}`);
    return response.data;
  }

  static async getCampaingList(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/tenant/campaign-list`, { params });
    return response.data;
  }

  static async getEmailsForCampaign(type: string, segmentId?: number) {
    const response = await axiosInstance.get(`/tenant/email-lists?type=${type}${segmentId ? `&typeId=${segmentId}` : ''}`);
    return response.data;
  }

  static async createCampaign(params: ReqParams = {}) {
    const response = await axiosInstance.post(`/tenant/create-campaign`, params);
    return response.data;
  }

  static async updateCampaign(params: ReqParams = {}) {
    const response = await axiosInstance.post(`/tenant/edit-campaign`, params);
    return response.data;
  }

  static async getCampaignDetails(id: number) {
    const respose = await axiosInstance.get(`/tenant/view-campaign/${id}`);
    return respose.data;
  }

  static async deleteCampaign(id: number) {
    const response = await axiosInstance.delete(`/tenant/campaign/${id}`, { skipGlobalLoader: true });
    return response.data;
  }
  // Lightweight agent list for dropdowns, e.g. { agentType: 'support' }
  static async getAgentDropdownList(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/tenant/agents/dropdown`, { params });
    return response.data;
  }

  static async toggleBanAgent(params: ReqParams = {}) {
    const response = await axiosInstance.post(`/tenant/agents/${params.id}/toggle-ban`, {
      reason: params.reason
    }, { skipGlobalLoader: true });
    return response.data;
  }

  static async deleteAgent(params: ReqParams = {}) {
    const response = await axiosInstance.delete(`/tenant/agents/${params.id}/delete`, {
      data: {
        reason: params.reason || '',
        force: params.force || false
      },
      skipGlobalLoader: true,
    });
    return response.data;
  }

  static async campaignBreakupList(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/tenant/view-campaign-emails/${params?.id}`, { params });
    return response.data;
  }

  static async downloadCustomerExampleCsv() {
    const response = await axiosInstance.get(`/tenant/download-sample-csv`);
    // console.log(response,"res");
    return response;
  }

  static async validateImportCustomerCsv(formData: FormData) {
    const response = await axiosInstance.post(`/tenant/validate-import-customers`, formData, { skipGlobalLoader: true });
    return response.data;
  }

  static async ImportCustomersFromCSV(formData: FormData) {
    const response = await axiosInstance.post(`/tenant/import-customers-from-csv`, formData, { skipGlobalLoader: true });
    return response.data;
  }


  /**
  * Get aggregated attendance report per agent
  * Maps AppTable/AppTenantTable date filters to {startDate,endDate}
  */
  static async getAgentsAttendanceReport(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/tenant/attendance/agents-report`, { params });
    return response.data;
  }

  /** Create the tenant's priority-based SLA policy. */
  static async addSlaPolicy(params: CreateSlaPolicyPayload) {
    const response = await axiosInstance.post(`/tenant/add-sla`, params);
    return response.data;
  }

  /** Get the tenant's active priority-based SLA policy. */
  static async getSlaPolicy(): Promise<SlaPolicyResponse> {
    const response = await axiosInstance.get(`/tenant/get-sla-by-userId`, {
      skipGlobalLoader: true,
    });
    return response.data;
  }

  /** Update the tenant's active priority-based SLA policy. */
  static async updateSlaPolicy(params: CreateSlaPolicyPayload) {
    const response = await axiosInstance.post(`/tenant/update-sla`, params);
    return response.data;
  }

  /** Remove the tenant's active priority-based SLA policy. */
  static async removeSlaPolicy() {
    const response = await axiosInstance.delete(`/tenant/remove-sla`);
    return response.data;
  }

  /** Live open / on-track / at-risk / breached counts for the SLA dashboard. */
  static async getSlaDashboardSummary() {
    const response = await axiosInstance.get(`/tenant/sla/dashboard-summary`, {
      suppressErrorDialog: true,
    });
    return response.data;
  }

  /** Open-ticket SLA position for each resolved priority, P1-P5. */
  static async getSlaPrioritySnapshot() {
    const response = await axiosInstance.get(`/tenant/sla/priority-snapshot`, {
      suppressErrorDialog: true,
    });
    return response.data;
  }

  /** Active tickets with their resolved SLA clock, for the SLA dashboard table. */
  static async getSlaTickets(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/tenant/sla/tickets`, {
      params: normalizeSlaTicketParams(params),
    });
    return response.data;
  }

  static async verifyReslove(params: ReqParams = {}) {
    const response = await axiosInstance.post(`/agent/ticket/resolve`, params, { skipGlobalLoader: true });
    return response.data;
  }

  static async getAvailableWhatsappTempelate(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/whatsapp/tenant-manual-template-list`, { params });
    return response.data;
  }

  static async getSalesQueryList(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/sales/sales-query-list`, { params });
    return response.data;
  }

  // Update the status of a general enquiry (new | contacted | closed)
  static async updateInquiryStatus(id: number | string, params: ReqParams = {}) {
    const response = await axiosInstance.patch(`/agent/inquiries/${id}/status`, params, { skipGlobalLoader: true });
    return response.data;
  }

  // Update the status of a sales enquiry (new | contacted | closed)
  static async updateSalesQueryStatus(id: number | string, params: ReqParams = {}) {
    const response = await axiosInstance.patch(`/sales/sales-query-list/${id}/status`, params, { skipGlobalLoader: true });
    return response.data;
  }

  // Delete a general enquiry
  static async deleteInquiry(id: number | string) {
    const response = await axiosInstance.delete(`/agent/inquiries/${id}`, { skipGlobalLoader: true });
    return response.data;
  }

  // Delete a sales enquiry
  static async deleteSalesQuery(id: number | string) {
    const response = await axiosInstance.delete(`/sales/sales-query-list/${id}`, { skipGlobalLoader: true });
    return response.data;
  }

  static async updateEmailTemeplate(params: ReqParams = {}) {
    const response = await axiosInstance.post(`/whatsapp/create-tenant-manual-template`, params);
    return response.data;
  }

  static async getEmailTemplateDetails(id: number) {
    const response = await axiosInstance.get(`/whatsapp/tenant-manual-template/${id}`);
    return response.data;
  }

  static async saveNotes(params: ReqParams = {}) {
    const response = await axiosInstance.post(`/agent/tickets/note-add`, params)
    return response.data;
  }

  //shift management
  static async masterShiftTypeList() {
    const response = await axiosInstance.get("/master/masterShiftTypeList");
    return response.data;
  }

  static async addShift(params: ReqParams = {}) {
    const response = await axiosInstance.post(`/tenant/add-shift`, params);
    return response.data;
  }

  static async shiftList(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/tenant/list-shift`, { params });
    return response.data;
  }

  static async shiftDelete(id: number) {
    const response = await axiosInstance.delete(`/tenant/delete-shift/${id}`, { skipGlobalLoader: true });
    return response.data;
  }

  static async shiftUpdate(id: number, params: ReqParams = {}) {
    const response = await axiosInstance.put(`/tenant/update-shift/${id}`, params);
    return response.data;
  }

  //rules setting
  static async getCurrentSummary() {
    const response = await axiosInstance.get("/tenant/view-tenant-scheduling-rule");
    return response.data;
  }

  static async updateRuleSettings(params: ReqParams = {}) {
    const response = await axiosInstance.post("/tenant/add-tenant-scheduling-rule", params);
    return response.data;
  }
  static async getCategoryList(params: ReqParams = {}) {
    const response = await axiosInstance.get("/sales/deal-category-list", { params });
    return response.data;
  }

  static async addCategory(params: ReqParams = {}) {
    const response = await axiosInstance.post("/sales/create-deal-category", params);
    return response.data;
  }

  static async updateCategory(id: number, params: ReqParams = {}) {
    const response = await axiosInstance.put(`/sales/update-deal-category/${id}`, params);
    return response.data;
  }

  static async deleteCategory(id: number) {
    const response = await axiosInstance.delete(`/sales/delete-deal-category/${id}`, { skipGlobalLoader: true });
    return response.data;
  }

  static async getRegionList(params: ReqParams = {}) {
    const response = await axiosInstance.get("/sales/regions-list", { params });
    return response.data;
  }

  static async addRegion(params: ReqParams = {}) {
    const response = await axiosInstance.post("/sales/create-regions", params);
    return response.data;
  }

  static async updateRegion(id: number, params: ReqParams = {}) {
    const response = await axiosInstance.put(`/sales/update-regions/${id}`, params);
    return response.data;
  }

  static async deleteRegion(id: number) {
    const response = await axiosInstance.delete(`/sales/delete-regions/${id}`, { skipGlobalLoader: true });
    return response.data;
  }

  static async getSubCategoryList(params: ReqParams = {}) {
    const response = await axiosInstance.get("/sales/deal-sub-category-list", { params });
    return response.data;
  }

  static async addSubCategory(params: ReqParams = {}) {
    const response = await axiosInstance.post("/sales/create-deal-sub-category", params);
    return response.data;
  }

  static async updateSubCategory(id: number, params: ReqParams = {}) {
    const response = await axiosInstance.put(`/sales/update-deal-sub-category/${id}`, params);
    return response.data;
  }

  static async deleteSubCategory(id: number) {
    const response = await axiosInstance.delete(`/sales/delete-deal-sub-category/${id}`, { skipGlobalLoader: true });
    return response.data;
  }

  static async getAgentShifts(params: { fromDate: string; toDate: string; }) {
    const response = await axiosInstance.get("/tenant/agent-shift-list-by-date-range", { params });
    return response.data;
  }

  static async subCategoryListByCategoryById(id: number) {
    const response = await axiosInstance.get(`/sales/sub-categories-by-category/${id}`);
    return response.data;
  }

  static async getDayViewAgentShifts(date: string) {
    const response = await axiosInstance.get(`/tenant/agent-shift-list-by-single-date?fromDate=${date}&toDate=${date}`);
    return response.data;
  }

  static async getTargetShifts(date: string, agentId: number) {
    const response = await axiosInstance.get(`/tenant/api-for-shift-swap-view?requesterAgentId=${agentId}&fromDate=${date}&toDate=${date}`);
    return response.data;
  }

  static async getTargetAgents(date: string, shiftId: number) {
    const response = await axiosInstance.get(`/tenant/api-for-shift-swap-view?targetShiftTypeId=${shiftId}&fromDate=${date}&toDate=${date}`);
    return response.data;
  }

  static async callSwapShift(params: ReqParams = {}) {
    const response = await axiosInstance.post("/tenant/single-day-shift-swap-by-admin", params, { skipGlobalLoader: true });
    return response.data;
  }

  static async getSwapRequests(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/tenant/shift-swap-request-list`, { params });
    return response.data;
  }

  static async approveRejectSwapRequest(params: ReqParams = {}) {
    const response = await axiosInstance.post(`/tenant/shift-swap-request-accept-reject`, params, { skipGlobalLoader: true });
    return response.data;
  }
  //dashboard grpahs
  static async getPipeLineByStage(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/sales/reports/pipeline-by-stage`, { params });
    return response.data;
  }
  static async getPipeLineStageValue(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/sales/reports/pipeline-value-by-Stage`, { params });
    return response.data;
  }
  static async getStats(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/sales/reports/KPI-summary`, { params });
    return response.data;
  }

  static async getPipeLineBySalesOwner(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/sales/reports/pipeline-by-owner`, { params });
    return response.data;
  }

  static async getDealAging(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/sales/reports/deal-aging-analysis`, { params });
    return response.data;
  }

  static async getForecastVsTarget(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/sales/reports/forecast-vs-target`, { params });
    return response.data;
  }

  static async getTrendAnalysis(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/sales/reports/deal-monthly-trend-analysis`, { params });
    return response.data;
  }
  static async getAgentPerformaceList(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/sales/reports/agent-rank-based-on-csat`, { params });
    return response.data;
  }

  static async addDealNote(params: ReqParams = {}) {
    const response = await axiosInstance.post(`/sales/deals/notes`, params);
    return response.data;
  }

  static async getDealNotes(id: number) {
    const response = await axiosInstance.get(`/sales/deals/notes/${id}`);
    return response.data;
  }

  static async getLeaveRequests(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/tenant/leave-request-list`, { params });
    return response.data;
  }

  static async approveRejectLeaveRequest(params: ReqParams = {}) {
    const response = await axiosInstance.post(`/tenant/accept-reject-leave-request`, params, { skipGlobalLoader: true });
    return response.data;
  }

  static async getStaffList(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/tenant/view-all-attendance-user-wise`, { params });
    return response.data;
  }

  static async getStaffDetails(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/tenant/staff-shift-list-details`, { params });
    return response.data;
  }

  static async createTicketCategory(params: ReqParams = {}) {
    const response = await axiosInstance.post("/tenant/ticket-category-create", params);
    return response.data;
  }

  static async getTicketCategories(params: ReqParams = {}) {
    const response = await axiosInstance.get("/tenant/ticket-category-list", { params });
    return response.data;
  }

  static async deleteTicketCategory(id: number) {
    const response = await axiosInstance.delete(`/tenant/ticket-category-delete/${id}`, { skipGlobalLoader: true });
    return response.data;
  }

  static async createTicketSubCategory(params: ReqParams = {}) {
    const response = await axiosInstance.post("/tenant/ticket-sub-category-create", params);
    return response.data;
  }

  static async updateTicketSubCategory(id: number, params: ReqParams = {}) {
    const response = await axiosInstance.put(`/tenant/ticket-sub-category-update/${id}`, params);
    return response.data;
  }

  static async getTicketSubCategories(params: ReqParams = {}) {
    const response = await axiosInstance.get("/tenant/ticket-sub-category-list", { params });
    return response.data;
  }

  static async deleteTicketSubCategory(id: number) {
    const response = await axiosInstance.delete(`/tenant/ticket-sub-category-delete/${id}`, { skipGlobalLoader: true });
    return response.data;
  }

  static async createTicketSubSubCategory(params: ReqParams = {}) {
    const response = await axiosInstance.post("/tenant/ticket-sub-sub-category-create", params);
    return response.data;
  }

  static async updateTicketSubSubCategory(id: number, params: ReqParams = {}) {
    const response = await axiosInstance.put(`/tenant/ticket-sub-sub-category-update/${id}`, params);
    return response.data;
  }

  static async getTicketSubSubCategories(params: ReqParams = {}) {
    const response = await axiosInstance.get("/tenant/ticket-sub-sub-category-list", { params });
    return response.data;
  }

  static async getTicketSubSubCategoryById(id: number) {
    const response = await axiosInstance.get(`/tenant/ticket-sub-sub-category-get-by-id/${id}`);
    return response.data;
  }

  static async deleteTicketSubSubCategory(id: number) {
    const response = await axiosInstance.delete(`/tenant/ticket-sub-sub-category-delete/${id}`, { skipGlobalLoader: true });
    return response.data;
  }
  static async getManagerList(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/tenant/managers`, { params });
    return response.data;
  }

  // Group Management
  static async getGroupList(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/tenant/groups`, {
      params: normalizeGroupListParams(params),
    });
    return response.data;
  }

  static async getAllGroups() {
    const response = await axiosInstance.get("/tenant/groups/all");
    return response.data;
  }

  static async addGroup(params: ReqParams = {}) {
    const response = await axiosInstance.post(`/tenant/groups`, params);
    return response.data;
  }

  static async getGroupMembers(groupId: number, params: ReqParams = {}) {
    const response = await axiosInstance.get(`/tenant/groups/${groupId}/members`, { params });
    return response.data;
  }

  static async getAllGroupMembers(params: ReqParams = {}) {
    const response = await axiosInstance.get("/tenant/members", {
      params: prepareMemberListParams(params),
    });
    return response.data;
  }

  // Shared by group-ticket assignment and ticket-list filtering. Callers only
  // invoke this after a group is selected.
  static async getGroupMemberOptions(groupId: number) {
    const response = await axiosInstance.get("/tenant/members/dropdown", {
      params: { groupId, memberType: "member" },
    });
    return response.data;
  }

  static async getGroupTicketQueue(params: ReqParams = {}) {
    const response = await axiosInstance.get("/tenant/groups/ticket-queue", {
      params: normalizeGroupTicketQueueParams(params),
    });
    return response.data;
  }

  static async getMemberAssignedTickets(params: ReqParams = {}) {
    const response = await axiosInstance.get("/tenant/members/assigned-tickets", {
      params: normalizeMemberAssignedTicketParams(params),
    });
    return response.data;
  }

  static async getGroupTicketFullHistory(ticketId: number) {
    const response = await axiosInstance.get(
      `/tenant/tickets/${ticketId}/full-history`,
    );
    return response.data;
  }

  static async getGroupTicketNotes(
    ticketId: number,
    params: { offset?: number; limit?: number } = {},
  ) {
    const response = await axiosInstance.get(
      `/group/tickets/${ticketId}/notes`,
      {
        params: { offset: params.offset ?? 0, limit: params.limit ?? 10 },
        skipGlobalLoader: true,
      },
    );
    return response.data;
  }

  static async getGroupTicketMetrics(ticketId: number) {
    const response = await axiosInstance.get(
      `/group/tickets/${ticketId}/metrics`,
      { skipGlobalLoader: true },
    );
    return response.data;
  }

  static async getTenantTicketMetrics(ticketId: number) {
    const response = await axiosInstance.get(
      `/tenant/tickets/${ticketId}/metrics`,
      { skipGlobalLoader: true },
    );
    return response.data;
  }

  static async getGroupTicketConversations(
    ticketId: number,
    params: { offset?: number; limit?: number } = {},
  ) {
    const response = await axiosInstance.get(
      `/group/tickets/${ticketId}/conversations`,
      {
        params: { offset: params.offset ?? 0, limit: params.limit ?? 10 },
        skipGlobalLoader: true,
      },
    );
    return response.data;
  }

  static async addTenantMember(params: ReqParams = {}) {
    const response = await axiosInstance.post("/tenant/members/add", params);
    return response.data;
  }

  static async checkGroupManager(groupId: number, memberType: "manager" | "member") {
    const response = await axiosInstance.get("/tenant/members/manager-check", {
      params: { groupId, memberType },
    });
    return response.data;
  }

  static async addGroupMember(groupId: number, params: ReqParams = {}) {
    const response = await axiosInstance.post(`/tenant/groups/${groupId}/members`, params);
    return response.data;
  }

  static async updateGroupMember(groupId: number, memberId: number, params: ReqParams = {}) {
    const response = await axiosInstance.put(`/tenant/groups/${groupId}/members/${memberId}`, params);
    return response.data;
  }

  static async deleteGroupMember(memberId: number) {
    const response = await axiosInstance.delete(`/tenant/members/${memberId}/delete`, { skipGlobalLoader: true });
    return response.data;
  }

  static async updateGroup(id: number, params: ReqParams = {}) {
    const response = await axiosInstance.put(`/tenant/groups/${id}`, params);
    return response.data;
  }

  static async deleteGroup(id: number) {
    const response = await axiosInstance.delete(`/tenant/groups/${id}`, { skipGlobalLoader: true });
    return response.data;
  }

  static async addManager(params: ReqParams = {}) {
    const response = await axiosInstance.post(`/tenant/managers`, params);
    return response.data;
  }

  static async updateManager(id: number, params: ReqParams = {}) {
    const response = await axiosInstance.put(`/tenant/managers/${id}`, params);
    return response.data;
  }

  static async toggleBanManager(params: ReqParams = {}) {
    const response = await axiosInstance.post(`/tenant/managers/${params.id}/toggle-ban`, {
      reason: params.reason
    }, { skipGlobalLoader: true });
    return response.data;
  }

  static async deleteManager(params: ReqParams = {}) {
    const response = await axiosInstance.delete(`/tenant/managers/${params.id}/delete`, {
      data: {
        reason: params.reason || '',
      },
      skipGlobalLoader: true,
    });
    return response.data;
  }

  static async deleteFeedbackSubmission(id: string) {
    const response = await axiosInstance.delete(`/tenant/feedback-submissions/${id}`, { skipGlobalLoader: true });
    return response.data;
  }

  static async getFeedbackDetails(id: string) {
    const response = await axiosInstance.get(`/tenant/feedback/submissions/${id}`);
    return response.data;
  }

  static async resendFeedback(invitationId: string) {
    const response = await axiosInstance.post(`/tenant/feedback/submissions/${invitationId}/resend`, undefined, { skipGlobalLoader: true });
    return response.data;
  }

  static async getActiveSubscription(userId: number) {
    const response = await axiosInstance.get(`/subscription/get-active-subscription/${userId}`);
    return response.data;
  }

  static async getInvoiceDetails(invoiceUuid: string) {
    const response = await axiosInstance.get(`/subscription/get-invoice-details/${invoiceUuid}`);
    return response.data;
  }

  static async getAllPlans() {
    const response = await axiosInstance.get("/subscription/get-all-plans");
    return response.data;
  }

  static async assignSubscription(params: ReqParams = {}) {
    const response = await axiosInstance.post(`/subscription/assign-subscription`, params, { skipGlobalLoader: true });
    return response.data;
  }

  static async paymentCallback(params: ReqParams = {}) {
    const response = await axiosInstance.post(`/subscription/payment-callback`, params, { skipGlobalLoader: true });
    return response.data;
  }

  static async getTenantPlanHistory(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/subscription/get-all-subscription-user`, { params });
    return response.data;
  }

  static async updateAutoPaymentStatus(params: { userId: number; autoPayment?: string; autopayment?: string }) {
    const response = await axiosInstance.post(`/subscription/auto-payment/status`, params, { skipGlobalLoader: true });
    return response.data;
  }

  static async getFacebookPageDetails(id: string) {
    const response = await axiosInstance.get(`/facebook-messenger/facebook-page-config-by-tenant/${id}`);
    return response.data;
  }

  // Facebook DM conversations inbox (supervisor view)
  static async getFacebookConversations(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/facebook-messenger/conversations`, { params });
    return response.data;
  }

  static async getFacebookConversationDetails(conversationId: string | number) {
    const response = await axiosInstance.get(`/facebook-messenger/conversations/${conversationId}`);
    return response.data;
  }

  // Manual reply to a Facebook DM conversation.
  // Body is multipart FormData: { message, attachment? } (at most one attachment).
  static async replyToFacebookConversation(conversationId: string | number, formData: FormData) {
    const response = await axiosInstance.post(
      `/facebook-messenger/conversations/${conversationId}/reply`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' }, skipGlobalLoader: true }
    );
    return response.data;
  }

  // Convert a Facebook DM conversation into a ticket.
  // selectionMode 'selected' attaches selectedMessageIds; 'all' attaches the whole thread.
  static async convertConversationToTicket(conversationId: string | number, payload: ReqParams = {}) {
    const response = await axiosInstance.post(
      `/facebook-messenger/conversations/${conversationId}/convert-to-ticket`,
      payload,
      { skipGlobalLoader: true }
    );
    return response.data;
  }

  // ===== Instagram DM conversations (mirror of the Facebook endpoints) =====
  static async getInstagramConversations(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/instagram-messenger/conversations`, { params });
    return response.data;
  }

  static async getInstagramConversationDetails(conversationId: string | number) {
    const response = await axiosInstance.get(`/instagram-messenger/conversations/${conversationId}`);
    return response.data;
  }

  static async replyToInstagramConversation(conversationId: string | number, formData: FormData) {
    const response = await axiosInstance.post(
      `/instagram-messenger/conversations/${conversationId}/reply`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' }, skipGlobalLoader: true }
    );
    return response.data;
  }

  static async convertInstagramConversationToTicket(conversationId: string | number, payload: ReqParams = {}) {
    const response = await axiosInstance.post(
      `/instagram-messenger/conversations/${conversationId}/convert-to-ticket`,
      payload,
      { skipGlobalLoader: true }
    );
    return response.data;
  }

  // ===== WhatsApp inbox conversations (mirror of the Facebook/Instagram endpoints) =====
  static async getWhatsappConversations(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/whatsapp/tenant/inbox-conversations`, { params });
    return response.data;
  }

  static async getWhatsappConversationDetails(conversationId: string | number) {
    const response = await axiosInstance.get(`/whatsapp/tenant/inbox-conversations/${conversationId}`);
    return response.data;
  }

  static async replyToWhatsappConversation(conversationId: string | number, formData: FormData) {
    const response = await axiosInstance.post(
      `/whatsapp/tenant/inbox-conversations/${conversationId}/reply`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' }, skipGlobalLoader: true }
    );
    return response.data;
  }

  static async convertWhatsappConversationToTicket(conversationId: string | number, payload: ReqParams = {}) {
    const response = await axiosInstance.post(
      `/whatsapp/tenant/inbox-conversations/${conversationId}/convert-to-ticket`,
      payload,
      { skipGlobalLoader: true }
    );
    return response.data;
  }

  // ===== Facebook / Instagram manual templates (mirror of the WhatsApp template flow) =====
  static async getFacebookTemplates(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/facebook-messenger/tenant-fb-template-list`, { params });
    return response.data;
  }

  static async getFacebookTemplateDetails(id: number | string) {
    const response = await axiosInstance.get(`/facebook-messenger/tenant-fb-template/${id}`);
    return response.data;
  }

  static async updateFacebookTemplate(id: number | string, params: ReqParams = {}) {
    const response = await axiosInstance.put(`/facebook-messenger/tenant-fb-template/${id}`, params);
    return response.data;
  }

  static async getInstagramTemplates(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/instagram-messenger/tenant-ig-template-list`, { params });
    return response.data;
  }

  static async getInstagramTemplateDetails(id: number | string) {
    const response = await axiosInstance.get(`/instagram-messenger/tenant-ig-template/${id}`);
    return response.data;
  }

  static async updateInstagramTemplate(id: number | string, params: ReqParams = {}) {
    const response = await axiosInstance.put(`/instagram-messenger/tenant-ig-template/${id}`, params);
    return response.data;
  }

  static async getTicketSummary(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/tenant/dashboard/ticket-summary`, { params });
    return response.data;
  }

  static async getTicketVolumeAnalysis(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/tenant/reports/ticket-volume-analysis`, { params });
    return response.data;
  }

  static async getGroupTicketVolumeReport(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/tenant/reports/group-ticket-volume`, {
      params,
      suppressErrorDialog: true,
    });
    return response.data;
  }

  static async getGroupTimeInGroupReport(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/tenant/reports/group-time-in-group`, {
      params,
      suppressErrorDialog: true,
    });
    return response.data;
  }

  // Section 1.2-1.4 breakdowns. These endpoints are still being built, so the
  // callers pass `suppressErrorDialog` and render their own pending state
  // instead of triggering the global error dialog.
  // Section 3 agent reports.
  static async getAgentPerformanceReport(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/tenant/reports/agent-performance`, { params });
    return response.data;
  }

  static async getAgentOmnichannelReport(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/tenant/reports/agent-omnichannel`, { params });
    return response.data;
  }

  static async getAgentComparisonReport(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/tenant/reports/agent-comparison`, { params });
    return response.data;
  }

  static async getAgentQaReport(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/tenant/reports/agent-qa`, { params });
    return response.data;
  }

  static async getIncomingTicketsByChannel(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/tenant/reports/incoming-tickets-by-channel`, {
      params,
    });
    return response.data;
  }

  static async getOpenTicketsByPriority(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/tenant/reports/open-tickets-by-priority`, {
      params,
    });
    return response.data;
  }

  static async getOpenTicketsByAge(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/tenant/reports/open-tickets-by-age`, { params });
    return response.data;
  }

  static async getTicketStatusMix(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/tenant/reports/ticket-status-mix`, { params });
    return response.data;
  }

  static async getResolutionTimeSpread(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/tenant/reports/resolution-time-spread`, { params });
    return response.data;
  }

  static async getAvgHandleTimeAnalysis(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/tenant/reports/avg-handle-time-analysis`, { params });
    return response.data;
  }

  static async getKpiSummary(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/tenant/reports/kpi-summary`, { params });
    return response.data;
  }

  static async privateReplyToFacebookComment(commentId: string, message: string) {
    const response = await axiosInstance.post(`/facebook-messenger/facebook-comment/${commentId}/private-reply`, { message }, { skipGlobalLoader: true });
    return response.data;
  }

  // Campaign Reports API Endpoints
  static async getCampaignOverview(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/tenant/campaign-reports/overview`, { params });
    return response.data;
  }

  static async getCampaignLeaderboard(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/tenant/campaign-reports/leaderboard`, { params });
    return response.data;
  }

  static async getCampaignAgentsReport(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/tenant/campaign-reports/agents`, { params });
    return response.data;
  }

  static async getCampaignAgentsTrend(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/tenant/campaign-reports/agents/trend`, { params });
    return response.data;
  }

  static async getCampaignTemplatesTrend(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/tenant/campaign-reports/templates/trend`, { params });
    return response.data;
  }

  static async getAdminTenantSubscriptionOverview(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/admin/dashboard/tenant-subscriptions/overview`, { params });
    return response.data;
  }

  static async getAdminRevenueSubscriptionTrend(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/admin/dashboard/tenant-subscriptions/revenue-subscription-trend`, { params });
    return response.data;
  }

  static async getAdminPlanDistribution(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/admin/dashboard/tenant-subscriptions/plan-distribution`, { params });
    return response.data;
  }

  static async getAdminTenantOnboardingTrend(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/admin/dashboard/tenant-subscriptions/tenant-onboarding-trend`, { params });
    return response.data;
  }

  // External API config/token management
  static async getExternalApiConfig(tenantId: number) {
    const response = await axiosInstance.get(`/tenant/external-api/config/${tenantId}`);
    return response.data;
  }

  static async getExternalApiPayloadPreview() {
    const response = await axiosInstance.get(`/tenant/external-api/payload-preview/tenant`);
    return response.data;
  }

  static async createExternalApiConfig(params: ReqParams = {}) {
    const response = await axiosInstance.post(`/tenant/external-api/config`, params);
    return response.data;
  }

  static async regenerateExternalApiToken(tenantId: number) {
    const response = await axiosInstance.post(`/tenant/external-api/config/${tenantId}/regenerate-token`, undefined, { skipGlobalLoader: true });
    return response.data;
  }
}
